import React from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { ROUTES } from "../../../routes/paths";
import {
  Button,
  EmptyStateProps,
  failedEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
// The same mark the clients screen shows over its own first-run state, so a
// workspace with no clients meets one invitation rather than two.
import { ReactComponent as FirstClientMark } from "../../../assets/svg/No data images/my client.svg";
import WelcomeCard from "../components/WelcomeCard";
import StatTile from "../components/StatTile";
import EarningsCard from "../components/EarningsCard";
import NewClientsCard from "../components/NewClientsCard";
import TotalClientsCard from "../components/TotalClientsCard";
import RecentClientsTable from "../components/RecentClientsTable";
import BrandWelcomeModal from "../components/BrandWelcomeModal";
import UpgradeBanner from "../components/UpgradeBanner";
import { useDashboard } from "../hooks/useDashboard";
import { useBrandSetUp } from "../hooks/useBrandSetUp";
// Plans are the brand feature's, and so is the read that says whether the
// workspace sells any — the checklist's second tick and the gate over Add
// client both ask it, so it lives where a plan is made rather than here.
import { usePlansCreated } from "../../brand/hooks/usePlansCreated";
import { useBrandWelcome } from "../hooks/useBrandWelcome";
import { useCurrentSubscription } from "../../billing/hooks/useCurrentSubscription";
import { gateReason } from "../../billing/hooks/useSubscriptionGate";
import { hasAccess } from "../../billing/subscriptionState";
import { isSetupComplete, setupSteps } from "../setupSteps";

/** What a tile shows in place of a count nobody has measured yet. */
const NO_FIGURE = "-";

const DashboardPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { summary, status, reload } = useDashboard();
  const { brandSetUp, status: brandStatus } = useBrandSetUp();

  // Whether anything is sold yet, for the checklist's middle step.
  const { plansCreated, status: plansStatus } = usePlansCreated();

  // What the workspace is actually on. Not a step of the checklist any more —
  // the upgrade banner below is what asks for a plan — but still what decides
  // that banner and what this screen's create buttons are allowed to do. The
  // trial comes with it because it is the other way the product is open.
  const {
    subscription,
    trial,
    accessSource,
    status: subscriptionStatus,
  } = useCurrentSubscription();

  // Only once the brand read has actually answered: a call that failed is not
  // a brand that is missing, and the greeting is not worth putting to someone
  // who set theirs up months ago. Same caution as the checklist below.
  const brandWelcome = useBrandWelcome(brandStatus === "ready" && !brandSetUp);

  /**
   * Whether the screen is still waiting on anything at all.
   *
   * All four reads, not just the dashboard's own: the welcome card is decided
   * by the brand and the plans, the banner by the subscription, and a card
   * that lands after the rest reads as the screen arriving twice. So they are
   * one answer as far as this screen is concerned — it waits for the slowest.
   */
  const loading =
    status === "loading" ||
    brandStatus === "loading" ||
    plansStatus === "loading" ||
    subscriptionStatus === "loading";

  // One phase drives every card on the screen — they wait together, shimmer
  // together, and land together. A dashboard that answers quickly never
  // reaches the shimmer at all.
  const phase = useLoadPhase(loading);

  // A workspace with no clients has no earnings and nothing converted either —
  // a client is what a paid request turns into — so one measured zero puts
  // every card on the screen into the state it opens a new workspace in.
  const noClientsYet = summary.totalClients === 0;

  // Whether there is a plan behind this workspace, which is what the upgrade
  // banner is about and nothing more — a workspace on its free trial has no
  // plan yet either, and the banner is the offer it is spending the fortnight
  // deciding on. What may be *done* here is `createLocked` below.
  //
  // Off `access_source`, the server's word for what is holding the door: a
  // plan is one only when the server says `SUBSCRIPTION`, whatever the record
  // beside it reads. False until the read has said so, which is what keeps the
  // banner off a screen that does not yet know whether there is anything to
  // upgrade from.
  const notSubscribed =
    subscriptionStatus === "ready" && accessSource !== "SUBSCRIPTION";

  /**
   * What everything on this screen that adds a client asks by — which is not
   * quite the fact above.
   *
   * A running trial opens the product as fully as a plan does, and the server
   * says which is holding the door in the same field: `SUBSCRIPTION`,
   * `FREE_TRIAL`, or `NONE` for the day the trial runs out with nothing bought
   * behind it, which is what shuts every entry point — here, on the three list
   * screens, and on the create-plan screen — leaving the workspace readable and
   * nothing more.
   *
   * This is the same line `useSubscriptionGate` draws for the other three
   * screens — kept in step with it by hand because this screen already holds
   * the billing answer and must not ask for it twice.
   */
  const createLocked =
    subscriptionStatus === "ready" && !hasAccess(accessSource);

  /**
   * Whether the fortnight is what has just run out — the fact behind both the
   * locked buttons' wording and the banner's, so it is worked out once here.
   *
   * `!subscription` is doing real work: a workspace that took the trial, bought
   * a plan and later cancelled it also has a finished trial on its record, and
   * telling that dietitian their trial has ended would name the wrong thing by
   * about a year. Same line `useSubscriptionGate` draws, kept in step by hand
   * because this screen already holds the billing answer.
   */
  const trialEnded = createLocked && !subscription && !!trial;

  /**
   * What the locked button says, which is not the same on both sides of the
   * fortnight: somebody whose trial ran out yesterday is pressing Add client
   * out of habit and is owed the reason they actually met, not a general note
   * about subscriptions. `gateReason` is the list screens' wording too, so the
   * dashboard does not invent a fourth way of saying the same thing.
   *
   * Worked out whether or not the button is locked — it is only read when it
   * is, and the alternative is a conditional around a string.
   */
  const lockedReason = gateReason("add a client", trialEnded);

  /** The checklist, or `null` where there is nothing left to tick and where any
   *  of the three reads it is worked out from failed — a step this screen
   *  cannot vouch for is not one to put in front of somebody. See
   *  `WelcomeCard`'s `setup`. */
  const setup = (() => {
    if (
      brandStatus !== "ready" ||
      plansStatus !== "ready" ||
      status !== "ready"
    ) {
      return null;
    }

    const steps = setupSteps({
      brandSetUp,
      plansCreated,
      hasClients: !noClientsYet,
      // The row is a link into the Add client screen, so it is shut by the same
      // fact as the button below it — otherwise the checklist would be the one
      // way in that a lapsed workspace still had.
      createLocked,
    });
    return isSetupComplete(steps) ? null : steps;
  })();

  // Both come straight from `GET /api/v1/dashboard/`. A null count is one the
  // backend has no figure for, and a "0" there would read as a measurement
  // rather than as a figure that does not exist yet.
  //
  // The tile counts `active_clients` rather than everyone on the books: it says
  // "Active clients", and the roster's whole size is what the pie card below
  // reports against the requests behind it.
  //
  // `emptyMessage` is only for the workspace that has not started — it is the
  // measured zero being put in words, and it is deliberately not what a null
  // gets: a figure nobody has counted is still the dash.
  const stats = [
    {
      id: "active-clients",
      label: "Active clients",
      value: summary.activeClients ?? NO_FIGURE,
      emptyMessage: noClientsYet ? "No active clients" : null,
    },
    {
      id: "plans-created",
      label: "Plans created",
      value: summary.plansCreated ?? NO_FIGURE,
      emptyMessage: noClientsYet ? "No plans created" : null,
    },
  ];

  /** What the recent clients table shows in place of rows. Nothing about
   *  loading: while the figures are out the table shimmers its rows, and the
   *  status line below is what says so in words.
   *
   *  There is no search on this table, so there are only the two cases: the
   *  dashboard call failed, or this workspace has no clients in it yet. */
  const recentEmptyState = (): EmptyStateProps =>
    status === "failed"
      ? failedEmptyState(
          "We could not load your dashboard",
          <Button size="sm" variant="secondary" onClick={reload}>
            Try again
          </Button>
        )
      : {
          // The same welcome the clients screen gives, and the same mark, so
          // the two read as one invitation rather than two different answers
          // to the same emptiness. No highlights here: this card is one band
          // of a full dashboard, and the list belongs on the screen the
          // button leads to. Without them the block centres — see `EmptyState`.
          variant: "welcome",
          illustration: <FirstClientMark aria-hidden className="w-11 h-11" />,
          title: "Add your first client",
          description:
            "Start building your client list to manage consultations, meal plans, and progress.",
          action: (
            <Button
              size="md"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate(ROUTES.addClient)}
              disabled={createLocked}
              title={createLocked ? lockedReason.heading : undefined}
              onDisabledClick={() =>
                toast.warning(lockedReason.heading, lockedReason.detail)
              }
            >
              Add client
            </Button>
          ),
        };

  return (
    <div className="px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Dashboard" />

      {/* Over the top of the screen rather than after it: this is the first
          thing a workspace with no brand is asked, and the dashboard behind it
          has nothing on it yet to interrupt. */}
      {brandWelcome.open && (
        <BrandWelcomeModal
          onSetupBrand={() => {
            brandWelcome.dismiss();
            navigate(ROUTES.myBrand);
          }}
          onDismiss={brandWelcome.dismiss}
        />
      )}

      {/* Said as soon as the request goes out, whether or not anything is
          drawn for it: this line is not seen, so it costs nothing to be early,
          and the blocks it stands for are hidden from screen readers — they
          are the shape of an answer rather than the answer. */}
      {loading && (
        <p role="status" className="sr-only">
          Loading your dashboard…
        </p>
      )}

      {/* Above the greeting, which is where the design puts it: it is the one
          thing on this screen that is not a figure about the practice, and a
          workspace with no plan meets it before anything it cannot use yet.
          Past the trial it reports the end of it rather than offering to keep
          something already gone — the same split the create buttons make. */}
      {notSubscribed && <UpgradeBanner trialEnded={trialEnded} />}

      {/* Whenever all three cards can't sit on one line, the welcome card takes a
          full row and the two stat tiles pair up beneath it — including on
          phones. From xl everything fits across as 3 + 1 + 1 of 5 columns. */}
      <div className="mt-5 grid gap-3 sm:gap-4 3xl:gap-5 grid-cols-2 xl:grid-cols-5">
        <div className="col-span-2 xl:col-span-3">
          <WelcomeCard
            highlights={{
              requests: summary.newRequestsInLast7Days,
              plans: summary.plansCreatedInLast7Days,
              renewals: summary.renewalsInNext7Days,
            }}
            setup={setup}
            phase={phase}
          />
        </div>
        {stats.map((stat) => (
          <StatTile
            key={stat.id}
            label={stat.label}
            value={stat.value}
            emptyMessage={stat.emptyMessage}
            phase={phase}
          />
        ))}
      </div>

      {/* The two chart cards get ~1.5x the width of the pie card, matching the
          design — an even 3-column split leaves the charts cramped. Three across
          only from xl; below that the pie card wraps to its own row. */}
      <div className="mt-4 3xl:mt-5 grid gap-4 3xl:gap-5 md:grid-cols-2 xl:grid-cols-[1.5fr_1.5fr_1fr]">
        <EarningsCard
          total={summary.totalIncome}
          data={summary.incomeByMonth}
          empty={noClientsYet}
          phase={phase}
        />
        <NewClientsCard
          data={summary.newClientsByMonth}
          empty={noClientsYet}
          phase={phase}
        />
        <TotalClientsCard
          paidClients={summary.activeClients}
          totalRequests={summary.totalRequests}
          conversionRate={summary.conversionRate}
          empty={noClientsYet}
          phase={phase}
        />
      </div>

      <div className="mt-4 3xl:mt-5">
        <RecentClientsTable
          clients={summary.recentClients}
          phase={phase}
          empty={recentEmptyState()}
        />
      </div>
    </div>
  );
};

export default DashboardPage;
